// Single source of truth for the brand palette, in both light and dark.
// These same values drive (a) CSS custom properties consumed by the hand-rolled
// CSS (App.css / GameCard.css) and (b) the MUI theme — so both halves of the app
// stay visually in sync and both respond to the light/dark toggle.

export type ThemeMode = 'light' | 'dark';

// Keys are full CSS custom-property names so they can be applied with
// element.style.setProperty(key, value) directly.
export const tokens: Record<ThemeMode, Record<string, string>> = {
  light: {
    '--color-bg': '#f4f6fb',
    '--color-surface': '#ffffff',
    '--color-surface-2': '#f6f8fc',
    '--color-text': '#1a2333',
    '--color-text-muted': '#5b6576',
    '--color-border': '#e4e8f0',

    '--color-primary': '#2a5298',
    '--color-primary-strong': '#1e3c72',
    '--color-primary-contrast': '#ffffff',
    '--color-accent': '#667eea',

    '--header-gradient': 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
    '--auth-gradient': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    '--submit-gradient': 'linear-gradient(135deg, #f59e0b 0%, #ff8f00 100%)',

    '--color-success': '#2e7d32',
    '--color-success-bg': '#e8f5e9',
    '--color-success-border': '#c3e6c3',
    '--color-success-text': '#1b5e20',

    '--color-warning': '#f59e0b',
    '--color-warning-bg': '#fff4d6',
    '--color-warning-border': '#ffe08a',
    '--color-warning-text': '#8a5a00',

    '--color-danger': '#d32f2f',
    '--color-danger-bg': '#fdecea',
    '--color-danger-border': '#f5c2c0',
    '--color-danger-text': '#a31515',

    '--color-info-bg': '#e3f2fd',
    '--color-info-text': '#1565c0',

    '--shadow-sm': '0 2px 8px rgba(0,0,0,0.08)',
    '--shadow-md': '0 4px 16px rgba(0,0,0,0.12)',
  },
  dark: {
    '--color-bg': '#0e131c',
    '--color-surface': '#171e2b',
    '--color-surface-2': '#1f2836',
    '--color-text': '#e7ecf5',
    '--color-text-muted': '#9aa5b8',
    '--color-border': '#2b3547',

    '--color-primary': '#6b8fd6',
    '--color-primary-strong': '#5578c0',
    '--color-primary-contrast': '#0b1220',
    '--color-accent': '#8ea2f0',

    '--header-gradient': 'linear-gradient(135deg, #16223c 0%, #1f3a63 100%)',
    '--auth-gradient': 'linear-gradient(135deg, #2c3357 0%, #3a2850 100%)',
    '--submit-gradient': 'linear-gradient(135deg, #d98a0a 0%, #b36b00 100%)',

    '--color-success': '#66bb6a',
    '--color-success-bg': '#16301c',
    '--color-success-border': '#2c5233',
    '--color-success-text': '#a5d6a7',

    '--color-warning': '#ffb74d',
    '--color-warning-bg': '#3a2c12',
    '--color-warning-border': '#5c4a24',
    '--color-warning-text': '#ffcc80',

    '--color-danger': '#ef5350',
    '--color-danger-bg': '#3a1c1c',
    '--color-danger-border': '#5c2b2b',
    '--color-danger-text': '#ef9a9a',

    '--color-info-bg': '#14293c',
    '--color-info-text': '#90caf9',

    '--shadow-sm': '0 2px 8px rgba(0,0,0,0.4)',
    '--shadow-md': '0 4px 16px rgba(0,0,0,0.5)',
  },
};

export const token = (mode: ThemeMode, name: string): string => tokens[mode][name];
